/**
 * Cart Bundle Nesting
 *
 * Line items sharing the same `properties._bundle_id` (set by
 * bundle-multi-color.js when a multi-quantity tier is added) are rendered
 * as one nested group (see cart-drawer-bundle-group.liquid /
 * cart-line-item-bundle-group.liquid).
 *
 * Two actions, both handled here directly via the raw Cart AJAX API, each
 * in a SINGLE network request:
 * - Trash button (`.cart-bundle-group__remove`) removes every line in the
 *   set at once.
 * - `+`/`-` stepper (`data-bundle-qty-increase` / `-decrease`) changes every
 *   line's quantity together, scaled by how many units of that colour make
 *   up one set (`data-unit-qty`, from bundle-multi-color.js's
 *   `_bundle_unit_qty` property), so the ratio between colours never
 *   drifts even when a colour was picked in more than one slot.
 *
 * Both read each line's current key/quantity/unit-qty straight off the
 * already-rendered `.cart-bundle-group__child` elements instead of
 * fetching /cart.js first — that DOM only ever reflects this same script's
 * own last section-HTML swap, so it's already current. Combined with
 * asking /cart/update.js to also render the section HTML in the same
 * response (`sections`/`sections_url` params — standard Shopify Ajax Cart
 * API, confirmed live for this store's 'cart-drawer'/'cart-template'
 * section IDs), this whole flow is one round trip instead of three (fetch
 * cart, update, re-fetch section HTML separately).
 *
 * Still exactly one mutation per action (not a sequence of them) — this
 * matters because a line's `key` (variant_id:properties_hash) isn't stable
 * across SEPARATE mutations if the product has an automatic discount tied
 * to quantity — sequential single-line changes (old approach) could act on
 * a stale key or an unevenly-updated set. Buttons are disabled
 * synchronously before the request starts to rule out double-clicks.
 *
 * None of these use the theme's own cart-item remove/qty-button classes
 * (.scd-item__remove / .scd-item__btn) on purpose — Minimog's own
 * delegated handlers (assets/app.js `cartItemSelectors`) act on a single
 * line by positional index into their own cached `this.cart`, so matching
 * those classes would race this handler instead of cooperating with it.
 */
(function () {
  'use strict';

  if (window.__cartBundleNestingBound) return;
  window.__cartBundleNestingBound = true;

  var updateUrl = '/cart/update.js';

  // Matches Minimog's own `cartSection` naming in assets/app.js
  // (this.cartSection = this.isCartPage ? 'cart-template' : 'cart-drawer')
  // — confirmed live: /cart/update.js with sections: ['cart-drawer',
  // 'cart-template'] returns rendered HTML for both on this store.
  function sectionIdFor(itemEl) {
    return itemEl.closest('#cart-drawer-container') ? 'cart-drawer' : 'cart-template';
  }

  function domBundleLines(bundleId) {
    // A slot's units aren't guaranteed to live on a single cart line — this
    // store's automatic quantity-tiered discount can split what we added as
    // one slot's worth of identical units across two+ lines (e.g. a 3x
    // same-colour slot landing as separate 2x/1x lines). `data-quantity` is
    // already the SUMMED total for the slot (see the *-bundle-group.liquid
    // snippets); `keys` lists every line that makes up that total.
    var lines = [];
    document.querySelectorAll('.cart-bundle-group__child[data-bundle-id="' + bundleId + '"]').forEach(function (el) {
      var keys = (el.getAttribute('data-keys') || '').split(',').filter(Boolean);
      if (!keys.length) return;
      lines.push({
        keys: keys,
        quantity: parseInt(el.getAttribute('data-quantity'), 10) || 0,
        unitQty: parseInt(el.getAttribute('data-unit-qty'), 10) || 1,
      });
    });
    return lines;
  }

  function updateLines(updates, sectionId) {
    return fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: sectionId, sections_url: window.location.pathname }),
    }).then(function (res) {
      // Without this check, a Shopify-side error (invalid quantity, cart
      // token issue, etc.) still resolves here with an error-shaped JSON
      // body that has no `.sections` — applySectionHTML then silently no-ops
      // instead of throwing, so .catch() never runs and any buttons already
      // disabled for this action are left stuck forever.
      if (!res.ok) return res.text().then(function (t) { throw new Error(t || ('HTTP ' + res.status)); });
      return res.json();
    });
  }

  function applySectionHTML(data, itemEl, sectionId) {
    // Throwing here (rather than silently returning) matters just as much as
    // the res.ok check in updateLines() — a response that's missing the
    // expected section, or a page whose markup doesn't match (.scd__body/
    // .scd__summary not found), must not look like a no-op success, or any
    // buttons disabled for this action are stuck relying on a swap that
    // never happens.
    var html = data.sections && data.sections[sectionId];
    if (!html) throw new Error('No section HTML for "' + sectionId + '" in cart response');

    var wrapper = itemEl.closest('.scd__wrapper') || document;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newBody = doc.querySelector('.scd__body');
    var newSummary = doc.querySelector('.scd__summary');
    var oldBody = wrapper.querySelector('.scd__body');
    var oldSummary = wrapper.querySelector('.scd__summary');
    if (!newBody || !oldBody) throw new Error('.scd__body not found while applying cart section update');
    oldBody.replaceWith(newBody);
    if (newSummary && oldSummary) oldSummary.replaceWith(newSummary);

    document.querySelectorAll('.sf-cart-count').forEach(function (el) {
      el.textContent = data.item_count;
    });

    if (window.MinimogEvents && typeof window.MinimogEvents.emit === 'function') {
      window.MinimogEvents.emit('ON_CART_UPDATE', data);
    }
  }

  function handleError(err) {
    console.error('[cart-bundle-nesting] Cart update failed, reloading:', err);
    window.location.reload();
  }

  // ─── Remove whole set ──────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest && e.target.closest('.cart-bundle-group__remove');
    if (!removeBtn) return;

    var itemEl = removeBtn.closest('[data-bundle-id]');
    if (!itemEl) return;

    var bundleId = removeBtn.getAttribute('data-bundle-id') || itemEl.getAttribute('data-bundle-id');
    if (!bundleId || removeBtn.disabled) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    removeBtn.disabled = true;

    var sectionId = sectionIdFor(itemEl);
    var updates = {};
    domBundleLines(bundleId).forEach(function (line) {
      line.keys.forEach(function (key) { updates[key] = 0; });
    });

    updateLines(updates, sectionId)
      .then(function (data) {
        applySectionHTML(data, itemEl, sectionId);
      })
      .catch(handleError)
      .finally(function () {
        removeBtn.disabled = false;
      });
  }, true);

  // ─── Set-count stepper (+/-) ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bundle-qty-increase], [data-bundle-qty-decrease]');
    if (!btn) return;

    var itemEl = btn.closest('[data-bundle-id]');
    if (!itemEl) return;

    var bundleId = btn.getAttribute('data-bundle-id') || itemEl.getAttribute('data-bundle-id');
    if (!bundleId || btn.disabled) return;

    var delta = btn.hasAttribute('data-bundle-qty-increase') ? 1 : -1;

    e.preventDefault();
    e.stopImmediatePropagation();

    var groupButtons = document.querySelectorAll(
      '[data-bundle-id="' + bundleId + '"][data-bundle-qty-increase], [data-bundle-id="' + bundleId + '"][data-bundle-qty-decrease]'
    );
    groupButtons.forEach(function (el) { el.disabled = true; });

    var sectionId = sectionIdFor(itemEl);
    var updates = {};
    domBundleLines(bundleId).forEach(function (line) {
      var target = Math.max(line.unitQty, line.quantity + (delta * line.unitQty));
      // If this slot is currently split across multiple lines, put the new
      // total on the first one and zero the rest out — consolidating back
      // to a single line each time, rather than letting the split compound.
      line.keys.forEach(function (key, idx) {
        updates[key] = idx === 0 ? target : 0;
      });
    });

    updateLines(updates, sectionId)
      .then(function (data) {
        applySectionHTML(data, itemEl, sectionId);
      })
      .catch(handleError)
      .finally(function () {
        // On success this swaps in a fresh row anyway (so re-enabling these
        // soon-to-be-discarded buttons is harmless); on any failure — a
        // rejected request, or a response that didn't actually contain the
        // section HTML — this is what stops the buttons from being stuck
        // disabled forever, since reopening the drawer only toggles CSS on
        // this same DOM rather than re-fetching it.
        groupButtons.forEach(function (el) { el.disabled = false; });
      });
  }, true);
})();
